import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';
import { Asset } from '../types';
import { assetService } from '../services';
import { dataSourceService, SecuritySearchResult } from '../services/dataSourceService';
import { useToast, useThrottle } from '../hooks';
import { useZodForm } from '../hooks';
import { assetSchema, AssetFormData as AssetZodFormData } from '../schemas';
import Card, { CardBody } from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import EmptyState from '../components/ui/EmptyState';
import PageTopBar from '../components/ui/PageTopBar';
import SearchBar from '../components/ui/SearchBar';
import Select from '../components/ui/Select';
import ConfirmationModal from '../components/common/ConfirmationModal';

// Icons
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const BuildingIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

const ChartIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const HomeIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
    />
  </svg>
);

const FolderIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
    />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const typeVariants: Record<string, 'primary' | 'secondary' | 'success' | 'warning'> = {
  company: 'primary',
  fund: 'success',
  property: 'warning',
  other: 'secondary',
};

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'company':
      return <BuildingIcon />;
    case 'fund':
      return <ChartIcon />;
    case 'property':
      return <HomeIcon />;
    default:
      return <FolderIcon />;
  }
};

const Assets: React.FC = () => {
  const toast = useToast();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Table filter state
  const [filterQuery, setFilterQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSector, setFilterSector] = useState('');

  // Sort state
  type AssetSortField = 'name' | 'type' | 'sector' | 'uid';
  type SortDir = 'asc' | 'desc';
  const [sortField, setSortField] = useState<AssetSortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (field: AssetSortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Search state (UI-only, not part of form)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SecuritySearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [providerDescription, setProviderDescription] = useState('');
  const [isFromProvider, setIsFromProvider] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const suppressDropdownRef = useRef(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useZodForm<AssetZodFormData>(assetSchema, {
    defaultValues: { name: '', description: '', dataUid: '', type: 'company', sector: '' },
    mode: 'onBlur',
  });

  const formDataUid = watch('dataUid');
  const formType = watch('type');
  const formSector = watch('sector');

  const throttledSearchQuery = useThrottle(searchQuery);

  // Search securities when query changes
  useEffect(() => {
    const search = async () => {
      if (throttledSearchQuery.length < 1) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const results = await dataSourceService.searchSecurities(throttledSearchQuery, 10);
        setSearchResults(results);
        if (suppressDropdownRef.current) {
          suppressDropdownRef.current = false;
        } else {
          setShowDropdown(true);
        }
      } catch (err) {
        console.error('Search failed:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    search();
  }, [throttledSearchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectSecurity = (security: SecuritySearchResult) => {
    setProviderDescription(security.description);
    setIsFromProvider(true);
    suppressDropdownRef.current = true;
    setSearchQuery(security.name);
    setShowDropdown(false);

    // Update form values — shouldValidate on name+dataUid to update isValid
    setValue('name', security.name, { shouldDirty: true, shouldValidate: true });
    setValue('dataUid', security.uid, { shouldDirty: true, shouldValidate: true });
    setValue('type', security.type.toLowerCase() as AssetZodFormData['type'], {
      shouldDirty: true,
    });
    setValue('sector', security.sector, { shouldDirty: true });
  };

  const handleNameChange = (value: string) => {
    setSearchQuery(value);
    setValue('name', value, { shouldDirty: true });

    // Any manual typing invalidates the provider selection
    if (isFromProvider) {
      setIsFromProvider(false);
      setProviderDescription('');
      setValue('dataUid', '', { shouldDirty: true, shouldValidate: true });
      setValue('type', 'company', { shouldDirty: true });
      setValue('sector', '', { shouldDirty: true });
    }
  };

  const loadAssets = async () => {
    try {
      setLoading(true);
      const response = await assetService.getAssets(1, 100);
      setAssets(response.data);
    } catch (err) {
      console.error('Failed to load assets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, []);

  const openCreateModal = () => {
    setEditingAsset(null);
    reset({ name: '', description: '', dataUid: '', type: 'company', sector: '' });
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    setProviderDescription('');
    setIsFromProvider(false);
    setIsModalOpen(true);
  };

  const openEditModal = async (asset: Asset) => {
    setEditingAsset(asset);
    suppressDropdownRef.current = true;
    setSearchQuery(asset.name);
    setSearchResults([]);
    setShowDropdown(false);
    setProviderDescription('');
    setIsFromProvider(false);
    setIsModalOpen(true);

    reset({
      name: asset.name,
      description: asset.notes || '',
      dataUid: asset.dataUid || '',
      type: asset.type,
      sector: asset.sector || '',
    });

    if (asset.dataUid) {
      try {
        const results = await dataSourceService.searchSecurities(asset.dataUid, 1);
        const match = results.find(r => r.uid === asset.dataUid);
        if (match) {
          setProviderDescription(match.description);
          setIsFromProvider(true);
        }
      } catch (err) {
        console.error('Failed to fetch security description:', err);
      }
    }
  };

  const onSubmit = async (data: AssetZodFormData) => {
    try {
      const payload = {
        name: data.name.trim(),
        dataUid: data.dataUid?.trim() || undefined,
        type: data.type,
        sector: data.sector?.trim() || undefined,
        notes: data.description?.trim() || undefined,
      };
      if (editingAsset) {
        await assetService.updateAsset(editingAsset.id, payload);
      } else {
        await assetService.createAsset(payload);
      }

      await loadAssets();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Failed to save asset:', err);
      toast.error('Failed to save asset. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await assetService.deleteAsset(id);
      await loadAssets();
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete asset:', err);
      toast.error('Failed to delete asset. Please try again.');
    }
  };

  // Derive unique types and sectors for filter dropdowns
  const uniqueTypes = useMemo(() => Array.from(new Set(assets.map(a => a.type))).sort(), [assets]);
  const uniqueSectors = useMemo(() => {
    const sectors = assets.map(a => a.sector).filter(Boolean) as string[];
    return Array.from(new Set(sectors)).sort();
  }, [assets]);

  // Filter + sort
  const filteredSortedAssets = useMemo(() => {
    let result = assets;

    // Filter
    if (filterQuery) {
      const q = filterQuery.toLowerCase();
      result = result.filter(a =>
        a.name.toLowerCase().includes(q) ||
        (a.dataUid && a.dataUid.toLowerCase().includes(q))
      );
    }
    if (filterType) {
      result = result.filter(a => a.type === filterType);
    }
    if (filterSector) {
      result = result.filter(a => a.sector === filterSector);
    }

    // Sort
    const sorted = [...result];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'type':
          cmp = a.type.localeCompare(b.type);
          break;
        case 'sector':
          cmp = (a.sector || '').localeCompare(b.sector || '');
          break;
        case 'uid':
          cmp = (a.dataUid || '').localeCompare(b.dataUid || '');
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [assets, filterQuery, filterType, filterSector, sortField, sortDir]);

  const hasActiveFilters = filterQuery || filterType || filterSector;

  const SortHeader: React.FC<{ field: AssetSortField; label: string }> = ({ field, label }) => (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer select-none hover:text-neutral-700 transition-colors"
      onClick={() => toggleSort(field)}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        <FontAwesomeIcon
          icon={sortField === field ? (sortDir === 'asc' ? faSortUp : faSortDown) : faSort}
          className={`w-3 h-3 ${sortField === field ? 'text-primary-500' : 'text-neutral-300'}`}
        />
      </span>
    </th>
  );

  if (loading) {
    return (
      <div className="h-full bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-neutral-50">
      <PageTopBar
        title="Coverage"
        description="Manage companies and assets you cover for workflow assignments and data lookups"
      />
      <div className="flex-1 overflow-y-auto">
      <div className="w-full px-6 lg:px-8 py-8">

        {/* Filters */}
        {assets.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <SearchBar
              value={filterQuery}
              onChange={setFilterQuery}
              placeholder="Search by name or UID..."
            />
            <Select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              options={uniqueTypes.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
              placeholder="All types"
              className="w-40"
            />
            <Select
              value={filterSector}
              onChange={e => setFilterSector(e.target.value)}
              options={uniqueSectors.map(s => ({ value: s, label: s }))}
              placeholder="All sectors"
              className="w-44"
            />
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={() => { setFilterQuery(''); setFilterType(''); setFilterSector(''); }}>
                Clear all
              </Button>
            )}

            {/* Primary action — pushed to the right */}
            <div className="ml-auto">
              <Button variant="primary" size="sm" onClick={openCreateModal}>
                Add Coverage
              </Button>
            </div>
          </div>
        )}

        {assets.length === 0 ? (
          <EmptyState
            title="No coverage yet"
            description="Add companies or assets you cover to assign workflows and pull external data."
            action={{
              label: 'Add Coverage',
              onClick: openCreateModal,
            }}
          />
        ) : (
          <Card variant="default">
            <div className="overflow-x-auto no-scrollbar">
              <table className="w-full">
                <thead>
                  <tr className="bg-neutral-50 border-y border-neutral-100">
                    <SortHeader field="name" label="Name" />
                    <SortHeader field="type" label="Type" />
                    <SortHeader field="sector" label="Sector" />
                    <SortHeader field="uid" label="UID" />
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {filteredSortedAssets.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-sm text-neutral-500">
                        No assets match your filters
                      </td>
                    </tr>
                  ) : filteredSortedAssets.map(asset => (
                    <tr key={asset.id} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-neutral-900">{asset.name}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={typeVariants[asset.type] || 'secondary'} size="sm">
                          {asset.type}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                        {asset.sector || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {asset.dataUid ? (
                          <code className="text-xs font-mono text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded">
                            {asset.dataUid}
                          </code>
                        ) : (
                          <span className="text-sm text-neutral-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(asset)}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded shadow-sm hover:bg-primary-700 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(asset.id)}
                            className="px-3 py-1.5 text-xs font-medium text-danger-600 border border-danger-300 rounded shadow-sm hover:bg-danger-50 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/70 transition-opacity"
              onClick={() => setIsModalOpen(false)}
            />
            <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 max-w-md w-full mx-auto z-10">
              <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-neutral-900">
                  {editingAsset ? 'Edit Coverage' : 'Add New Coverage'}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  <CloseIcon />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                {/* Name Search Field */}
                <div className="relative">
                  <Input
                    ref={searchInputRef}
                    label="Name"
                    required
                    value={searchQuery}
                    onChange={e => handleNameChange(e.target.value)}
                    onBlur={() => trigger(['name', 'dataUid'])}
                    placeholder="Start typing to search..."
                    error={!!(errors.name || errors.dataUid)}
                    errorMessage={errors.name?.message || errors.dataUid?.message}
                    rightIcon={
                      isSearching ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent" />
                      ) : undefined
                    }
                  />

                  {/* Search Dropdown */}
                  {showDropdown && searchResults.length > 0 && (
                    <div
                      ref={dropdownRef}
                      className="absolute z-20 w-full mt-1 bg-surface border border-neutral-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      {searchResults.map(security => (
                        <button
                          key={security.uid}
                          type="button"
                          onClick={() => handleSelectSecurity(security)}
                          className="w-full px-3 py-2.5 text-left hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium text-neutral-900">{security.name}</span>
                              <span className="ml-2 text-xs text-neutral-500">{security.type}</span>
                            </div>
                            <code className="text-xs font-mono text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">
                              {security.uid}
                            </code>
                          </div>
                          <p className="text-xs text-neutral-500 mt-0.5 truncate">
                            {security.description}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* UID and Provider Description */}
                {formDataUid && (
                  <div className="px-3 py-2.5 bg-neutral-50 rounded-lg border border-neutral-200 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-neutral-600">UID:</span>
                      <code className="text-sm font-mono font-medium text-primary-700">
                        {formDataUid}
                      </code>
                    </div>
                    {providerDescription && (
                      <p className="text-sm text-neutral-600">{providerDescription}</p>
                    )}
                  </div>
                )}

                {/* User Notes */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">Notes</label>
                  <textarea
                    {...register('description')}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg transition-all resize-none"
                    placeholder="Add your own notes about this coverage"
                  />
                </div>

                {/* Type and Sector */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      Type
                    </label>
                    {isFromProvider || editingAsset ? (
                      <div className="w-full h-10 px-3 text-sm border border-neutral-200 rounded-lg bg-neutral-50 flex items-center text-neutral-600 capitalize">
                        {formType}
                      </div>
                    ) : (
                      <select
                        {...register('type')}
                        className="w-full h-10 px-3 text-sm border border-neutral-200 rounded-lg transition-all bg-surface"
                      >
                        <option value="company">Company</option>
                        <option value="fund">Fund</option>
                        <option value="property">Property</option>
                        <option value="other">Other</option>
                      </select>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      Sector
                    </label>
                    {isFromProvider || editingAsset ? (
                      <div className="w-full h-10 px-3 text-sm border border-neutral-200 rounded-lg bg-neutral-50 flex items-center text-neutral-600">
                        {formSector || '-'}
                      </div>
                    ) : (
                      <input
                        type="text"
                        {...register('sector')}
                        className="w-full h-10 px-3 text-sm border border-neutral-200 rounded-lg transition-all"
                        placeholder="e.g., Technology"
                      />
                    )}
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t border-neutral-200">
                  <div>
                    {editingAsset && (
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => setDeleteConfirm(editingAsset.id)}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" size="sm" onClick={() => setIsModalOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      disabled={isSubmitting || !isValid || !isDirty}
                    >
                      {isSubmitting ? 'Saving...' : editingAsset ? 'Save Changes' : 'Add Coverage'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onAction={async () => {
          if (!deleteConfirm) return;
          await handleDelete(deleteConfirm);
          setIsModalOpen(false);
        }}
        title="Delete Coverage"
        description="Are you sure you want to delete this coverage? This action cannot be undone."
        actionText="Delete"
      />
    </div>
  );
};

export default Assets;
